"use client";

import {
  createEntityView,
  getEntityViewList,
  updateEntityView,
  type EntityViewListResponse,
  type CreateEntityViewInput,
  EntityView,
  getEntityViewById,
  Section,
} from "@/services/entity-view.service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAnalytics } from "./use-analytics";
import { getDefaultViewId } from "./use-dashboard-view";
import { useMemo } from "react";
import { useEntitiesFromDb } from "./schema/use-entities-from-db";
import { useMyAros } from "./schema/use-my-aros";
import { useHasRootPriviliges } from "./auth/use-has-root-priviliges";
import { defaultWidthAndHeightForDataType, systemFields } from "@/lib/utils";

export const useEntityViewList = (entityId?: string) => {
  const { capture } = useAnalytics();
  const queryClient = useQueryClient();
  const { allAvailableEntities } = useEntitiesFromDb();
  const { data: myAros } = useMyAros();
  const { hasRootPriviliges: iMayApplyTemplates } = useHasRootPriviliges();

  // Middleware guarantees auth
  const { data: entityViews } = useQuery<EntityViewListResponse>({
    queryKey: ["entity-views", entityId],
    queryFn: () => getEntityViewList(entityId),
    enabled: !!entityId,
  });

  const createMutation = useMutation({
    mutationFn: (params: CreateEntityViewInput) =>
      createEntityView({
        ...params,
        entity: entityId ? { id: entityId } : undefined,
      }),
    onSuccess: () => {
      capture("create_entity_view");
      queryClient.invalidateQueries({
        queryKey: ["entity-views", entityId],
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateEntityView,
    onSuccess: () => {
      capture("update_entity_view");
      queryClient.invalidateQueries({
        queryKey: ["entity-views", entityId],
      });
    },
  });

  // Generate default view similar to dashboard views
  const defaultView: EntityView | undefined = useMemo(() => {
    if (!entityId || !allAvailableEntities?.length) return undefined;
    const entity = allAvailableEntities.find((e) => e?.id === entityId);
    if (!entity?.entity_fields?.data) return undefined;

    // Convert entity fields to Field type for the default view
    const entityFields = entity.entity_fields.data
      .filter((field) => !systemFields.includes(field.name))
      .map((field) => ({
        id: field.id,
        name: field.name,
        type: field.data_type,
        icon: field.form_type ?? field.data_type,
        width: defaultWidthAndHeightForDataType(field.data_type).width,
        height: defaultWidthAndHeightForDataType(field.data_type).height,
      }));

    // Convert entity relations to Field type
    const entityRelations =
      entity.entity_relations?.data?.map((relation) => ({
        id: relation.id,
        name: relation.field_name,
        type: "relation" as const,
        icon: "relation" as const,
        width: 3 as const,
        height: 1 as const,
      })) || [];

    // Convert entity related (reverse relations) to Field type
    const entityRelated =
      entity.entity_related?.data?.map((relation) => ({
        id: relation.id,
        name: relation.target_field_name ?? "",
        type: "relation" as const,
        icon: "relation" as const,
        width: 3 as const,
        height: 1 as const,
      })) || [];

    // Combine all fields into a single section
    const allFields = [...entityFields, ...entityRelations, ...entityRelated];

    // Create single section with all fields
    const sections: Section[] = [
      {
        id: "default-all-fields-section",
        name: "all Fields",
        fields: allFields,
      },
    ];

    return {
      id: getDefaultViewId(entityId),
      name: "all fields",
      description: "Default view with all fields and relations",
      entity: { id: entityId },
      section_config: sections,
      pylo_aros: [],
    };
  }, [entityId, allAvailableEntities]);

  // Combine entity views with default view, filtering by permissions
  const calculatedEntityViews = useMemo(() => {
    const filteredViews =
      entityViews?.data?.filter((v) => {
        if (iMayApplyTemplates) {
          return true;
        }
        // If no AROs are connected to the view, everyone can see it
        if (!v.pylo_aros || v.pylo_aros.length === 0) {
          return true;
        }
        // Otherwise, check if user has any of the connected AROs
        return v.pylo_aros?.some((aro) => myAros?.data?.some((myAro) => myAro.id === aro.id));
      }) ?? [];

    const combined = [...filteredViews];
    if (defaultView) {
      combined.push(defaultView);
    }

    return { data: combined.filter((v) => !!v) };
  }, [entityViews?.data, defaultView, iMayApplyTemplates, myAros?.data]);

  return {
    entityViews: calculatedEntityViews,
    createEntityView: createMutation.mutate,
    updateEntityView: updateMutation.mutate,
    createEntityViewAsync: createMutation.mutateAsync,
    updateEntityViewAsync: updateMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    defaultView,
  };
};

export const useEntityView = (viewId: string) => {
  const { capture } = useAnalytics();
  const queryClient = useQueryClient();

  // Middleware guarantees auth
  const { data: entityView } = useQuery<EntityView>({
    queryKey: ["entity-view", viewId],
    queryFn: () => getEntityViewById(viewId),
  });
  const updateMutation = useMutation({
    mutationFn: updateEntityView,
    onSuccess: () => {
      capture("update_entity_view");
      queryClient.invalidateQueries({
        queryKey: ["entity-view", viewId],
      });
    },
  });

  const cache = queryClient.getQueryCache();
  const entityViewQueries = Array.from(cache.getAll()).filter((query) => {
    const queryKey = query.queryKey as unknown[];
    return queryKey[0] === "entity-views";
  });

  const entityFromCache = entityViewQueries.reduce<EntityView | undefined>((found, query) => {
    if (found) return found;

    const data = query.state.data as EntityViewListResponse | undefined;
    if (!data?.data) return undefined;

    return data.data.find((view: EntityView) => view.id === viewId);
  }, undefined);

  return {
    // We usually alredy have that view in the cache from viewing the list. So we can return that while loading the view by id
    entityView: entityView ?? entityFromCache,
    updateEntityViewAsync: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
};
